/**
 * Settings store for AgentOps application.
 * Manages theme, layout, and UI settings based on Vuexy structure.
 * Uses Zustand for state management with localStorage persistence.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Mode, Skin, Layout, LayoutComponentWidth } from '@core/types';
import themeConfig from '@configs/themeConfig';

/**
 * Settings state interface
 */
export interface Settings {
  mode: Mode;
  skin: Skin;
  semiDark: boolean;
  layout: Layout;
  navbarContentWidth: LayoutComponentWidth;
  contentWidth: LayoutComponentWidth;
  footerContentWidth: LayoutComponentWidth;
  primaryColor?: string;
}

/**
 * Settings store interface
 */
interface SettingsStore {
  settings: Settings;
  updateSettings: (settings: Partial<Settings>) => void;
  resetSettings: () => void;
  getIsSettingsChanged: () => boolean;
}

/**
 * Initial settings based on theme config
 */
const initialSettings: Settings = {
  mode: themeConfig.mode,
  skin: themeConfig.skin,
  semiDark: themeConfig.semiDark,
  layout: themeConfig.layout,
  navbarContentWidth: themeConfig.navbar.contentWidth,
  contentWidth: themeConfig.contentWidth,
  footerContentWidth: themeConfig.footer.contentWidth,
};

/**
 * Settings store with Zustand
 */
export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set, get) => ({
      settings: initialSettings,
      updateSettings: (newSettings: Partial<Settings>) => {
        set((state) => ({
          settings: { ...state.settings, ...newSettings },
        }));
      },
      resetSettings: () => {
        set({ settings: initialSettings });
      },
      getIsSettingsChanged: () => {
        const current = get().settings;
        return JSON.stringify(initialSettings) !== JSON.stringify(current);
      },
    }),
    {
      name: themeConfig.settingsCookieName,
    }
  )
);

/**
 * Hook to get settings (matches Vuexy's useSettings pattern)
 */
export const useSettings = () => {
  const store = useSettingsStore();
  return {
    settings: store.settings,
    updateSettings: store.updateSettings,
    resetSettings: store.resetSettings,
    isSettingsChanged: store.getIsSettingsChanged(),
  };
};
